import { api } from "./client";

// ── Users ────────────────────────────────────────────────────────────────
export interface AdminUser {
  id: string;
  email: string;
  name: string;
  picture_url: string | null;
  disabled: boolean;
  last_login_at: string | null;
}

export interface UserCreateBody {
  email: string;
  name?: string;
  grant?: { app_slug: string; role: string };
  group_ids?: string[];
}

export interface UserGrant {
  id: string;
  app_slug: string;
  app_name: string;
  role: string;
}

export interface UserDetail extends AdminUser {
  grants: UserGrant[];
  group_ids: string[];
}

export const usersApi = {
  list: (limit = 50, offset = 0, q?: string) =>
    api
      .get<{ users: AdminUser[]; total: number }>("/api/admin/users", {
        params: { limit, offset, ...(q?.trim() ? { q: q.trim() } : {}) },
      })
      .then((r) => r.data),
  get: (userId: string) =>
    api.get<UserDetail>(`/api/admin/users/${userId}`).then((r) => r.data),
  create: (body: UserCreateBody) =>
    api.post<AdminUser>("/api/admin/users", body).then((r) => r.data),
  setRole: (userId: string, appSlug: string, role: string | null) =>
    api
      .put(`/api/admin/users/${userId}/roles`, { app_slug: appSlug, role })
      .then(() => undefined),
  setDisabled: (userId: string, disabled: boolean) =>
    api
      .patch<AdminUser>(`/api/admin/users/${userId}`, { disabled })
      .then((r) => r.data),
  revokeSessions: (userId: string) =>
    api
      .post<{ revoked: number }>(`/api/admin/users/${userId}/sessions/revoke`)
      .then((r) => r.data),
};

// ── Groups ───────────────────────────────────────────────────────────────
export interface AdminGroup {
  id: string;
  slug: string;
  name: string;
  description: string | null;
}

export const groupsApi = {
  list: () =>
    api.get<{ groups: AdminGroup[] }>("/api/admin/groups").then((r) => r.data),
  create: (body: { slug: string; name: string; description?: string }) =>
    api.post<AdminGroup>("/api/admin/groups", body).then((r) => r.data),
  delete: (groupId: string) =>
    api.delete(`/api/admin/groups/${groupId}`).then(() => undefined),
  addMember: (groupId: string, userId: string) =>
    api
      .post(`/api/admin/groups/${groupId}/members`, { user_id: userId })
      .then(() => undefined),
  removeMember: (groupId: string, userId: string) =>
    api
      .delete(`/api/admin/groups/${groupId}/members/${userId}`)
      .then(() => undefined),
};

// ── Apps + role assignments ──────────────────────────────────────────────
export interface AdminApp {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon_url: string | null;
  url: string;
  roles: string[];
  sort_order: number | null;
}

export interface RoleGrant {
  id: string;
  app_slug: string;
  principal_type: "user" | "group";
  principal_id: string;
  role: string;
}

export const appsApi = {
  list: () =>
    api.get<{ apps: AdminApp[] }>("/api/admin/apps").then((r) => r.data),
  grants: (appSlug?: string) =>
    api
      .get<RoleGrant[]>("/api/admin/apps/grants", {
        params: appSlug ? { app_slug: appSlug } : {},
      })
      .then((r) => r.data),
  grant: (body: {
    app_slug: string;
    principal_type: "user" | "group";
    principal_id: string;
    role: string;
  }) =>
    api
      .post<{ id: string }>("/api/admin/apps/grants", body)
      .then((r) => r.data),
  revokeGrant: (assignmentId: string) =>
    api
      .delete(`/api/admin/apps/grants/${assignmentId}`)
      .then(() => undefined),
};

// ── Audit log ────────────────────────────────────────────────────────────
export interface AuditRow {
  id: string;
  action: string;
  actor_user_id: string | null;
  target_type: string | null;
  target_id: string | null;
  meta: Record<string, unknown>;
  created_at: string;
}

export const auditApi = {
  list: (action?: string, limit = 100) =>
    api
      .get<{ rows: AuditRow[] }>("/api/admin/audit", {
        params: { action, limit },
      })
      .then((r) => r.data),
};
